<template>
  <v-menu
    v-model="menu"
    :max-width="menuMinWidth"
    :min-width="menuMinWidth"
    ref="menu"
    bottom
    :offset-y="true"
    :close-on-click="false"
    :close-on-content-click="false"
    origin="center center"
    transition="scale-transition">
    <template v-slot:activator="{ on }">
      <v-btn
        v-on="on"
        v-bind="btnProps"
      >
        <v-icon class="date-picker-icon-calendar mr-2">$icons_product_calendar</v-icon>
        <span v-if="isRangeSet" class="btn-date-display">
          <span class="date-display">
            {{ startDisplay }}
          </span>
          <v-icon class="date-picker-icon-arrow">mdi-arrow-right-thick</v-icon>
          <span class="date-display">
            {{ endDisplay }}
          </span>
        </span>
        <span v-if="!isRangeSet">
          {{ $t('misc.noSelection') }}
        </span>
      </v-btn>
    </template>
    <div class="pickers-panel">
      <div class="panel-header primary white--text">
        <div class="panel-header--range-selection" v-if="isRangeSet">
          <span class="range-label grey--text text--lighten-2">{{ $t('misc.start') }}</span>
          <span class="date-display">
            {{ startDisplay }}
          </span>
          <v-icon class="date-picker-icon-arrow white--text">mdi-arrow-right-thick</v-icon>
          <span class="range-label grey--text text--lighten-2">{{ $t('misc.end') }}</span>
          <span class="date-display">
            {{ endDisplay }}
          </span>
        </div>
      </div>
      <div class="range-pickers">
        <v-container fluid>
          <v-row no-gutters>
            <v-col cols="12" sm="6">
              <v-date-picker id="start-picker" ref="start-picker"
                :locale="locale"
                v-model="pickerStart"
                :events="highlightedDates"
                :event-color="highlightedDatesCss"
                color="primary"
                width="100%" :show-current="false" :no-title="true" class="elevation-0" @change="onStartDateSelect($event)">
              </v-date-picker>
            </v-col>
            <v-col cols="12" sm="6">
              <v-date-picker id="end-picker" ref="end-picker"
                            :locale="locale"
                            v-model="pickerEnd"
                            :events="highlightedDates"
                            :event-color="highlightedDatesCss"
                            :min="endMin" color="primary" width="100%" :show-current="false" :no-title="true" class="elevation-0" @change="onEndDateSelect($event)"></v-date-picker>
            </v-col>
          </v-row>
        </v-container>
      </div>
      <div class="pickers-footer">
        <v-row justify="end">
          <v-btn text @click="cancel">{{ $t('misc.cancel') }}</v-btn>
          <v-btn color="primary" @click="apply">{{ $t('misc.confirm') }}</v-btn>
        </v-row>
      </div>
    </div>
  </v-menu>
</template>

<script>
import { isMobile } from '@/mixins/responsive.mixins';
import { FORMAT_DATE, FORMAT_UI_DATE, fromDateStrToMoment } from '@/util/time';

export default {
  name: 'date-range-picker',
  mixins: [isMobile],
  data() {
    return {
      menu: false,
      // The start date of the picker at the beginning of the session
      sessionPickerStart: null,
      // The end date of the picker at the beginning of the session
      sessionPickerEnd: null,
      // The model of the start picker
      pickerStart: null,
      // The model of the end picker
      pickerEnd: null,
      // Start date as a Moment object
      startDate: null,
      // End date as a Moment object
      endDate: null,
      endMin: null,
      highlightedDates: [],
      highlightedDatesCss: [],
    };
  },
  props: {
    // Allow the parent component to specify CSS classes for the button
    // that displays the date range picker.
    btnProps: {
      type: Object,
      default: () => ({
        small: false,
        color: 'default',
      }),
    },
    highlightColor: {
      type: String,
      default: 'primary lighten-3',
    },
    displayFormat: {
      type: String,
      default: FORMAT_UI_DATE,
    },
    locale: {
      type: String,
      default: 'en-uk',
    },
    parentStart: {
      type: String,
    },
    parentEnd: {
      type: String,
    },
  },
  computed: {
    menuMinWidth() {
      if (this.isMobile) {
        return '100%';
      }
      return '570px';
    },
    isRangeSet() {
      return this.startDate && this.endDate;
    },
    startDisplay() {
      return this.startDate.locale(this.locale).format(this.displayFormat);
    },
    endDisplay() {
      return this.endDate.locale(this.locale).format(this.displayFormat);
    },
  },
  created() {
    if (this.isParentRangeSet() && this.isParentRangeValid()) {
      this.setPickerRangeFromParentRange();
      // Set the "session" picker models
      this.sessionPickerStart = this.pickerStart;
      this.sessionPickerEnd = this.pickerEnd;
      this.highlightRange();
    }
  },
  methods: {
    setPickerRangeFromParentRange() {
      // Initialise the picker v-model attributes.
      this.pickerStart = this.parentStart;
      this.pickerEnd = this.parentEnd;

      // Initialise the Moment representation of the start and end models.
      this.startDate = fromDateStrToMoment(this.pickerStart);
      this.endDate = fromDateStrToMoment(this.pickerEnd);
    },
    setPickerRangeFromSessionRange() {
      // Initialise the picker v-model attributes.
      this.pickerStart = this.sessionPickerStart;
      this.pickerEnd = this.sessionPickerEnd;

      // Initialise the Moment representation of the start and end models.
      this.startDate = fromDateStrToMoment(this.pickerStart);
      this.endDate = fromDateStrToMoment(this.pickerEnd);
    },
    apply() {
      this.menu = false;

      /*
       * Override the session picker dates.
       * When the user begins another session with this date range picker
       * and cancels, the date range will fallback to the session picker dates.
       */
      this.sessionPickerStart = this.pickerStart;
      this.sessionPickerEnd = this.pickerEnd;

      this.$emit('rangeChanged', {
        start: this.pickerStart,
        end: this.pickerEnd,
      });
    },
    cancel() {
      this.menu = false;

      // Fallback to the session picker date ranges if they are set.
      if (this.sessionPickerStart && this.sessionPickerEnd) {
        this.setPickerRangeFromSessionRange();
      } else {
        this.pickerStart = null;
        this.pickerEnd = null;
        this.startDate = null;
        this.endDate = null;
      }
      // Reset the highlighted dates.
      this.highlightRange();
    },
    onStartDateSelect(date) {
      // Update the startDate model.
      this.startDate = fromDateStrToMoment(date);
      this.adjustEndDate();
      this.adjustEndDateMin();
      this.highlightRange();
    },
    onEndDateSelect(date) {
      // Update the endDate model.
      this.endDate = fromDateStrToMoment(date);
      this.highlightRange();
    },
    adjustEndDate() {
      if (!this.endDate || this.startDate.isAfter(this.endDate)) {
        // Reset the MomentJS endDate
        this.endDate = this.startDate.clone();
        // Reset the v-model of the end pate picker.
        this.pickerEnd = this.endDate.format(FORMAT_DATE);
      }
    },
    isParentRangeSet() {
      return this.parentStart && this.parentEnd;
    },
    isParentRangeValid() {
      const start = fromDateStrToMoment(this.parentStart);
      const end = fromDateStrToMoment(this.parentEnd);
      return start.isSameOrBefore(end);
    },
    /**
     * Adjust the earliest end date that may be selected
     * based on the current selected start date.
     * This is to prevent the user from selecting an end date earlier than the start date.
      */
    adjustEndDateMin() {
      this.endMin = this.startDate.format(FORMAT_DATE);
    },
    resetHighlights() {
      this.highlightedDates = [];
      this.highlightedDatesCss = {};
    },
    highlightRange() {
      if (!this.isRangeSet) {
        this.resetHighlights();
        return;
      }
      // eslint-disable-next-line no-unused-vars
      const dates = [];
      // A map of each day in the range and its associated CSS classes.
      const classes = {};

      const cursor = this.startDate.clone();

      while (cursor.isSameOrBefore(this.endDate)) {
        dates.push(cursor.format(FORMAT_DATE));
        // The CSS classes that will correspond to the current highlighted date.
        const dateClasses = [];

        dateClasses.push('v-date-range__in-range');

        dateClasses.push(this.highlightColor);

        classes[cursor.format(FORMAT_DATE)] = dateClasses.join(' ');
        // increment the cursor.
        cursor.add(1, 'day');
      }

      this.highlightedDates = dates;
      this.highlightedDatesCss = classes;
    },
  },
};
</script>

<style scoped lang="scss">
  .pickers-panel {
    background: #F9F9F9;
    /*padding: 1rem;*/

    .pickers-footer {
      padding: 1rem 2rem;
      border-top: 1px solid rgba(0,0,0,0.1);
    }
  }
  .btn-date-display {
    display: flex;
    flex-flow: row;
    align-items: center;
    .date-picker-icon-arrow {
      font-size: 14px;
    }

    /*&.primary .date-picker-icon-arrow {*/
    /*  color: #ffffff;*/
    /*}*/
  }
  .panel-header {
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 1rem 1rem;
    .panel-header--range-selection {
      display: flex;
      flex-direction: row;
      align-items: center;

      .date-picker-icon-arrow {
        padding: 0px 0.8rem;
      }

      .range-label {
        display: inline-block;
        padding-right: 7px;
      }

      .date-display {
        display: inline-block;
        font-weight: 500;
        letter-spacing: 0.25px;
      }
    }
  }
</style>
<style lang="scss">
.pickers-panel {
  .v-picker__body {
    background-color: #f9f9f9;
  }
}
</style>
